import { useDiagnosticReportById } from '@ctw/shared/api/fhir/diagnostic-report';
import type { DiagnosticReportModel } from '@ctw/shared/api/fhir/models/diagnostic-report';
import { ContentCard } from '@ctw/shared/components/containers/content-card';
import { DefinitionList } from '@ctw/shared/components/definition-list';
import { ResponsiveSourceDocumentLink } from '@ctw/shared/content/CCDA/responsive-source-document-link';
import {
  DiagnosticReportObservationList,
  diagnosticReportData,
} from '@ctw/shared/content/diagnostic-reports/helpers/observation-list';
import type { UseResourceDrawerOpenDrawerArgs } from '@ctw/shared/context/resource-drawer-context';
import { tw } from '@ctw/shared/utils/tailwind';

export const diagnosticReportDrawerAction: UseResourceDrawerOpenDrawerArgs<'DiagnosticReport'>['action'] =
  ({ model, requestContext }) => ({
    label: 'Diagnostic Report',
    title: model.displayName,
    content: {
      body: <DiagnosticReportDrawerBody liteModel={model} builderId={requestContext.builderId} />,
    },
  });

interface DiagnosticReportDrawerBodyProps {
  liteModel: DiagnosticReportModel;
  builderId: string;
}

// The list opens the drawer with a "lite" model fetched via the trimmed
// DiagnosticReportOverview fragment, which omits performer.resource and
// subject.resource. Re-fetch the full resource here so the Details card can
// show Performer and Data Source. Falls back to the lite model while loading
// so title/date and the observations section render immediately.
const DiagnosticReportDrawerBody = ({ liteModel, builderId }: DiagnosticReportDrawerBodyProps) => {
  const fullQuery = useDiagnosticReportById(liteModel.id);
  const model = fullQuery.data ?? liteModel;

  return (
    <div className={tw`flex flex-col gap-5`}>
      <ContentCard
        variant="focal"
        title="Details"
        content={{
          header: <ResponsiveSourceDocumentLink model={model} />,
        }}
      >
        <DefinitionList items={diagnosticReportData(model, builderId)} />
      </ContentCard>
      <DiagnosticReportObservationList model={model} />
    </div>
  );
};
